import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Layers, Save, RotateCcw, RefreshCcw, Plus, X, GripVertical,
  Sparkles, DollarSign, Star, ChevronDown, ShieldCheck, Infinity as InfinityIcon,
} from "lucide-react";

// Editable schema — mirrors the backend allowlist in admin_plans.py.
const FIELDS_TOP = [
  { key: "name",         label: "Plan name",       type: "text",       placeholder: "Founder Membership" },
  { key: "eyebrow",      label: "Eyebrow tag",     type: "text",       placeholder: "Short caption above the name" },
  { key: "tagline",      label: "Tagline",         type: "textarea",   placeholder: "One-sentence pitch shown under the plan name" },
  { key: "best_for",     label: "Best for",        type: "textarea",   placeholder: "Who is this plan ideal for?" },
  { key: "cta_label",    label: "CTA button label", type: "text",      placeholder: "Join Founder" },
];
const FIELDS_PRICE = [
  { key: "monthly",         label: "Monthly ($/mo)", type: "number", step: "0.01" },
  { key: "yearly",          label: "Yearly ($/yr)",  type: "number", step: "0.01" },
  { key: "enrollment_fee",  label: "Enrollment fee ($)", type: "number", step: "0.01" },
];
const FIELDS_META = [
  { key: "tier_rank",  label: "Tier rank",  type: "number", step: "1" },
  { key: "featured",   label: "Featured?",  type: "toggle" },
];

export default function AdminPlanBuilder() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [payload, setPayload] = useState(null); // {baseline, overrides, effective}
  const [draft, setDraft] = useState(null);      // current in-progress edit
  const [catalog, setCatalog] = useState({ features: [], limits: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      const found = (data.plans || []).find((p) => p.baseline.slug === slug);
      if (!found) {
        toast.error(`Unknown plan: ${slug}`);
        nav("/admin?tab=plans");
        return;
      }
      setPayload(found);
      // Draft starts from the effective (merged) view so admins edit "what
      // users see". Save only sends fields that differ from baseline.
      setDraft({ ...found.effective });
      setCatalog(data.entitlement_catalog || { features: [], limits: [] });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const dirty = useMemo(() => {
    if (!draft || !payload) return false;
    return JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(payload.effective));
  }, [draft, payload]);

  const overriddenFields = useMemo(() => {
    if (!draft || !payload) return new Set();
    const s = new Set();
    for (const key of Object.keys(payload.baseline || {})) {
      const b = payload.baseline[key];
      const d = draft[key];
      if (JSON.stringify(normalize({ [key]: b })[key]) !== JSON.stringify(normalize({ [key]: d })[key])) {
        s.add(key);
      }
    }
    return s;
  }, [draft, payload]);

  const set = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Send only the editable fields — API strips anything not on its allowlist.
      const body = {};
      const keys = [...FIELDS_TOP, ...FIELDS_PRICE, ...FIELDS_META].map((f) => f.key);
      keys.push("features", "excludes", "entitlements");
      for (const k of keys) if (draft[k] !== undefined && draft[k] !== null) body[k] = draft[k];
      await api.put(`/admin/plans/${slug}`, body);
      toast.success("Plan saved — changes are live on the pricing page.");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  const revert = () => { if (payload) setDraft({ ...payload.effective }); };

  const resetToBaseline = async () => {
    if (!window.confirm(`Reset ${slug} to the built-in defaults? All overrides for this plan are dropped.`)) return;
    try {
      await api.post(`/admin/plans/${slug}/reset`);
      toast.success("Plan reset to baseline.");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading || !payload || !draft) {
    return (
      <AppShell title="Plan Builder">
        <p className="text-sm text-muted-foreground">Loading plan…</p>
      </AppShell>
    );
  }

  return (
    <AppShell title="Plan Builder">
      <div className="w-full">
        <Link to="/admin?tab=plans" data-testid="plan-builder-back"
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary transition mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to plans
        </Link>

        <PageHero
          eyebrow="Plan builder"
          icon={Layers}
          title={<>Editing <span className="text-grad">{payload.baseline.name}</span></>}
          subtitle={`Slug: ${payload.baseline.slug}. Overrides only touch marketing copy + pricing — Stripe subscription products are keyed by slug and untouched.`}
          testid="plan-builder"
          right={
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={resetToBaseline}
                data-testid="plan-builder-reset"
                className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
                title="Restore all defaults for this plan"
              >
                <RefreshCcw className="w-3.5 h-3.5" /> Reset to baseline
              </button>
              {dirty && (
                <>
                  <button onClick={revert} disabled={saving} data-testid="plan-builder-revert"
                    className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Revert
                  </button>
                  <button onClick={save} disabled={saving} data-testid="plan-builder-save"
                    className="btn-amber inline-flex items-center gap-1 text-xs disabled:opacity-60">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                  </button>
                </>
              )}
            </div>
          }
        />

        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <Panel title="Copy" icon={Sparkles}>
              {FIELDS_TOP.map((f) => (
                <Field
                  key={f.key} f={f}
                  value={draft[f.key] ?? ""}
                  onChange={(v) => set(f.key, v)}
                  overridden={overriddenFields.has(f.key)}
                  baseline={payload.baseline[f.key]}
                />
              ))}
            </Panel>

            <Panel title="Pricing" icon={DollarSign}>
              <div className="grid grid-cols-3 gap-3">
                {FIELDS_PRICE.map((f) => (
                  <Field
                    key={f.key} f={f}
                    value={draft[f.key] ?? 0}
                    onChange={(v) => set(f.key, Number(v) || 0)}
                    overridden={overriddenFields.has(f.key)}
                    baseline={payload.baseline[f.key]}
                    compact
                  />
                ))}
              </div>
            </Panel>

            <Panel title="Features & exclusions" icon={Star}
              subtitle="These bullets show up on the pricing page and upgrade grid.">
              <ListEditor
                label="Features"
                items={draft.features || []}
                onChange={(items) => set("features", items)}
                testid="plan-builder-features"
                overridden={overriddenFields.has("features")}
                baselineCount={(payload.baseline.features || []).length}
              />
              <ListEditor
                label="Excludes (highlighted in the upgrade grid)"
                items={draft.excludes || []}
                onChange={(items) => set("excludes", items)}
                testid="plan-builder-excludes"
                overridden={overriddenFields.has("excludes")}
                baselineCount={(payload.baseline.excludes || []).length}
              />
            </Panel>

            <Panel title="Meta" icon={Layers}>
              <div className="grid grid-cols-2 gap-3 items-center">
                {FIELDS_META.map((f) => (
                  <Field
                    key={f.key} f={f}
                    value={draft[f.key] ?? (f.type === "toggle" ? false : 0)}
                    onChange={(v) => set(f.key, f.type === "toggle" ? !!v : Number(v) || 0)}
                    overridden={overriddenFields.has(f.key)}
                    baseline={payload.baseline[f.key]}
                  />
                ))}
              </div>
            </Panel>

            <Panel
              title="Entitlements"
              icon={ShieldCheck}
              subtitle="Per-plan feature toggles + monthly/daily usage caps. Enforced across databases, Morgan, and the sidebar."
            >
              <EntitlementsEditor
                value={draft.entitlements || { features: {}, limits: {}, custom: [] }}
                onChange={(next) => set("entitlements", next)}
                catalog={catalog}
              />
            </Panel>
          </div>

          {/* Live preview */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
              Live preview
            </p>
            <PlanPreviewCard plan={draft} />
            <p className="text-[10.5px] text-muted-foreground mt-3 leading-relaxed">
              This mirrors how the plan renders on the pricing page + upgrade grid.
              Changes save immediately once you click <b>Save</b>.
            </p>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Panel wrapper
// ---------------------------------------------------------------------------
function Panel({ title, icon: Icon, subtitle, children }) {
  return (
    <div className="surface p-5">
      <div className="flex items-center gap-2 pb-3 border-b border-shell-border/60">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <p className="font-display text-lg">{title}</p>
      </div>
      {subtitle && <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>}
      <div className="mt-4 space-y-3">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field renderer
// ---------------------------------------------------------------------------
function Field({ f, value, onChange, overridden, baseline, compact = false }) {
  const testid = `plan-builder-field-${f.key}`;
  return (
    <label className="block">
      <span className={["block text-[10.5px] uppercase tracking-widest font-bold mb-1",
        overridden ? "text-pink-600" : "text-muted-foreground"].join(" ")}>
        {f.label} {overridden && <span title={`Baseline: ${JSON.stringify(baseline)}`}>*</span>}
      </span>
      {f.type === "textarea" ? (
        <textarea
          rows={compact ? 2 : 3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder}
          data-testid={testid}
          className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/50"
        />
      ) : f.type === "toggle" ? (
        <button
          type="button"
          onClick={() => onChange(!value)}
          data-testid={testid}
          role="switch"
          aria-checked={!!value}
          className={["relative h-6 w-11 rounded-full transition-colors",
            value ? "bg-primary" : "bg-muted"].join(" ")}
        >
          <span className={["absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
            value ? "translate-x-5" : "translate-x-0.5"].join(" ")} />
        </button>
      ) : (
        <input
          type={f.type}
          step={f.step}
          value={value ?? ""}
          onChange={(e) => onChange(f.type === "number" ? e.target.value : e.target.value)}
          placeholder={f.placeholder}
          data-testid={testid}
          className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/50"
        />
      )}
    </label>
  );
}

// ---------------------------------------------------------------------------
// List editor — reorderable + add/remove for features / excludes
// ---------------------------------------------------------------------------
function ListEditor({ label, items, onChange, testid, overridden, baselineCount }) {
  const [dragId, setDragId] = React.useState(null);

  const set = (idx, val) => {
    const next = [...items];
    next[idx] = val;
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, ""]);
  const move = (from, to) => {
    if (from === to || to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="mb-4 last:mb-0" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between mb-2">
        <p className={["text-[10.5px] uppercase tracking-widest font-bold",
          overridden ? "text-pink-600" : "text-muted-foreground"].join(" ")}>
          {label} {overridden && <span title={`Baseline had ${baselineCount} items`}>*</span>}
        </p>
        <span className="text-[10px] text-muted-foreground">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={["group flex items-center gap-2 rounded-md border border-border bg-white px-2 py-1.5",
              dragId === idx ? "opacity-60" : ""].join(" ")}
            draggable
            onDragStart={() => setDragId(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragId !== null) move(dragId, idx); setDragId(null); }}
            onDragEnd={() => setDragId(null)}
            data-testid={`${testid}-item-${idx}`}
          >
            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0 cursor-move" />
            <input
              value={item}
              onChange={(e) => set(idx, e.target.value)}
              className="flex-1 text-sm bg-transparent focus:outline-none min-w-0"
              placeholder="Feature description"
            />
            <button
              onClick={() => remove(idx)}
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-50 text-rose-500 transition-opacity"
              title="Remove"
              data-testid={`${testid}-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={add}
        data-testid={`${testid}-add`}
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
      >
        <Plus className="w-3.5 h-3.5" /> Add {label.toLowerCase().split(" ")[0]}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview card
// ---------------------------------------------------------------------------
function PlanPreviewCard({ plan }) {
  return (
    <div className={["card-elevated p-5 relative", plan.featured ? "ring-2 ring-primary/30" : ""].join(" ")}>
      {plan.featured && (
        <span className="absolute top-3 right-3 text-[9.5px] font-bold uppercase tracking-widest text-primary">
          Featured
        </span>
      )}
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
        {plan.eyebrow || "\u00A0"}
      </p>
      <p className="font-display text-2xl mt-1">{plan.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{plan.tagline}</p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-bold">${Number(plan.monthly) || 0}</span>
        <span className="text-sm text-muted-foreground">/ mo</span>
        {Number(plan.yearly) > 0 && (
          <span className="text-xs text-muted-foreground ml-2">${plan.yearly}/yr</span>
        )}
      </div>
      {Number(plan.enrollment_fee) > 0 && (
        <p className="text-xs text-muted-foreground mt-1">+ ${plan.enrollment_fee} enrollment</p>
      )}
      <ul className="mt-4 space-y-1.5">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="text-sm flex items-start gap-2">
            <span className="text-primary mt-0.5">✓</span>
            <span className="flex-1">{f}</span>
          </li>
        ))}
      </ul>
      {(plan.excludes || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {plan.excludes.map((e, i) => (
            <li key={i} className="text-xs flex items-start gap-2 text-muted-foreground">
              <span className="mt-0.5">✕</span>
              <span className="flex-1">{e}</span>
            </li>
          ))}
        </ul>
      )}
      {plan.best_for && (
        <p className="mt-4 pt-3 border-t border-border text-xs italic text-muted-foreground">
          Best for: {plan.best_for}
        </p>
      )}
      <button
        disabled
        className="btn-primary w-full mt-4 text-sm opacity-70 cursor-not-allowed"
      >
        {plan.cta_label || "Choose plan"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function normalize(obj) {
  if (!obj) return {};
  const clean = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) clean[k] = v.filter((x) => x != null && String(x).trim() !== "");
    else if (typeof v === "number") clean[k] = Number(v);
    else if (typeof v === "boolean") clean[k] = !!v;
    else if (typeof v === "string") clean[k] = v.trim();
    else clean[k] = v;
  }
  return clean;
}


// ---------------------------------------------------------------------------
// Entitlements editor — feature toggles + numeric limits per plan.
// ---------------------------------------------------------------------------
function EntitlementsEditor({ value, onChange, catalog }) {
  const features = value?.features || {};
  const limits = value?.limits || {};

  // Group features by their `group` field so admins can scan by area.
  const grouped = (catalog.features || []).reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  const toggleFeature = (key, next) => {
    onChange({
      ...value,
      features: { ...features, [key]: !!next },
    });
  };
  const setLimit = (key, next) => {
    onChange({
      ...value,
      limits: { ...limits, [key]: Number.isFinite(next) ? next : 0 },
    });
  };

  return (
    <div className="space-y-5" data-testid="plan-builder-entitlements">
      {/* Features */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          Feature access
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(grouped).map(([groupName, feats]) => (
            <div key={groupName} className="rounded-md border border-shell-border/60 p-3">
              <p className="text-[10.5px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                {groupName}
              </p>
              <div className="space-y-1.5">
                {feats.map((f) => {
                  const on = !!features[f.key];
                  return (
                    <label
                      key={f.key}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      data-testid={`plan-feature-${f.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => toggleFeature(f.key, e.target.checked)}
                        className="w-3.5 h-3.5 accent-primary"
                      />
                      <span className={on ? "" : "text-muted-foreground line-through"}>
                        {f.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Numeric limits */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          Usage limits
        </p>
        <p className="text-[11px] text-muted-foreground mb-3">
          Use <span className="font-mono">-1</span> for unlimited, <span className="font-mono">0</span> to block entirely.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {(catalog.limits || []).map((l) => {
            const v = limits[l.key];
            const displayVal = v === -1 ? "" : (v ?? 0);
            const unlimited = v === -1;
            return (
              <div key={l.key} className="rounded-md border border-shell-border/60 p-3">
                <p className="text-xs font-semibold">{l.label}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {l.group}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    value={displayVal}
                    onChange={(e) => setLimit(l.key, Number(e.target.value))}
                    disabled={unlimited}
                    className="w-24 rounded-md border border-shell-border px-2 py-1 text-sm disabled:bg-muted/40"
                    data-testid={`plan-limit-${l.key}`}
                    placeholder={unlimited ? "∞" : "0"}
                  />
                  <label className="text-[11px] text-muted-foreground inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={unlimited}
                      onChange={(e) => setLimit(l.key, e.target.checked ? -1 : 0)}
                      className="w-3 h-3 accent-primary"
                      data-testid={`plan-limit-unlimited-${l.key}`}
                    />
                    <InfinityIcon className="w-3 h-3" /> Unlimited
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
